import { PROGRAMS_BUDGET_MAP } from 'components/common/vendorConfig';
import Zoom from 'components/common/zoom';
import type { ProfileBundleForCommPreference } from 'components/common/zoomUtil';
import type { Bundle, BundleEntry, CodeableConcept, Coding, Patient } from 'fhir/r4';
import { forwardRef, useEffect, useRef, useState } from 'react';
import React from 'react';
import { Segment, Tab, Sidebar, Grid, Modal, Header, Icon, Button } from 'semantic-ui-react';
import type { TabProps } from 'semantic-ui-react';
import useSWR from 'swr';
import type { ProgramBudget, ReferralSummary } from 'types';
import type { Fetched } from 'types/food';
import type { PractitionerEntry, ResourceEntry } from 'utils/bundleNarrowing';
import { asResourceEntries } from 'utils/bundleNarrowing';
import { resolveEnrollmentProgramDates } from 'utils/enrollmentCounterUtils';

import { useAuthContext } from '../../contexts/AuthContext';
import FHIRFetcher from '../../data/FHIRFetcher';
import * as FHIRUtils from '../../utils/FHIRUtils';
import Food from '../common/food';
import FoodCard from '../common/foodCard';
import Referrals from '../common/referrals';

import ActivityLog from './activityLog';
import PatientBanner from './banner';
import CarePlanComp from './careplan';
import Communications from './communication';
import Notes from './notes';
import Profile from './profile';
import Questionnaires from './questionnaires';
import Questionnaire from './questionnaires/questionnaire';
import type { QuestionnaireProps } from './questionnaires/questionnaire';
import type { QuestionnairesProps } from './questionnaires/types';
import type { CommunicationsBundle, Patient360Props } from './types';



const Patient360 = forwardRef((props: Patient360Props, _ref) => {
  const authInfo = useAuthContext();
  const fhirPatient: Patient = props.patient;
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any --
     `CarePlanComp` is a `forwardRef` with no exported handle type, and its `ref` prop
     is typed `Ref<CarePlanComp>`, which `useRef<unknown>` does not satisfy. The one
     call through the handle is cast at its read. */
  const tasksRef = useRef<any>();

  const [sidebarVisible, setSidebarVisible] = React.useState(props.showSideBar);

  const [activeTab, setActiveTab] = useState('');
  const [flatpatient, setFlatpatient] = useState(FHIRUtils.parseFHIRPatient(fhirPatient));

  // `Bundle | Record<string, never>` is this app's established spelling for a state
  // seeded with `{}` and later filled from a fetch; the consuming `profileData` props
  // in food/catalog, food/orders, food/draftOrders and foodRecommendations already
  // declare exactly that union. `undefined` joins it because the SWR `onSuccess`
  // passes `Bundle | undefined`.
  const [profileData, setProfileData] = useState<Bundle | Record<string, never> | undefined>({});
  /*
   * Both hold the `{ type, status }` projection built in the profile `onSuccess`
   * below, a subset of `ReferralSummary`'s fields.
   *
   * The initial value is the empty OBJECT, not an empty array and not `undefined`:
   * that is what the first render hands to the consuming `referralData` props, and
   * the profile `onSuccess` assigns `undefined` whenever the bundle has no `entry`.
   * The declared type spans all three states so the initialiser can stay exactly as
   * written — both consuming props (`careplan`, `questionnaires`) take `unknown`,
   * so nothing downstream constrains the shape.
   */
  const [activeReferralData, setActiveReferralData] = useState<
    ReferralSummary[] | Record<string, never> | undefined
  >({});
  const [activeAndCompletedReferralData, setActiveAndCompletedReferralData] = useState<
    ReferralSummary[] | Record<string, never> | undefined
  >({});
  const [allReferralData, setAllReferralData] = useState<ReferralSummary[] | undefined>([]);
  //redFlagWarning is used to have red background in patient banner if there is any red flag.
  //redFlagPopUpPage is used to show popup page when entering the member page
  //flagOpen is to control flag editor status, if we have flag editor open, we should not show the red flag popup page.
  const [redFlagWarning, setRedFlagWarning] = useState(false);
  const [redFlagPopUpPage, setRedFlagPopUpPage] = useState(false);
  const [flagOpen, setFlagOpen] = useState(false);
  const [doNotContact, setDoNotContact] = useState(false);
  const [provenanceCreateDate, setProvenanceCreateDate] = useState(''); // program week anchor date (week 1 day 1)
  const [lastInterventionLaunchDate, setLastInterventionLaunchDate] = useState(''); // enrollment launch date (executionPeriod.start)
  const [mobileUser, setMobileUser] = useState(false);
  // The two consuming props (`food`, `foodCard`) both declare
  // `setProgramBudget: (budget: ProgramBudget[]) => void`, which is the type the
  // task `onSuccess` below builds.
  const [programBudget, setProgramBudget] = useState<ProgramBudget[]>([]);
  const [referralId, setReferralId] = useState<string | false>(false);

  useEffect(() => {
    if (redFlagWarning && !flagOpen) {
      setRedFlagPopUpPage(true);
    }
  }, [redFlagWarning, flagOpen]);

  // Both are `unknown[]` — the raw `fhirpath.evaluate` arrays. Interpolating them
  // comma-joins each, so a member with two given names gets "a,b" in the tab title.
  window.document.title = `${flatpatient.firstName as unknown as string} ${
    flatpatient.lastName as unknown as string
  }`;

  const handleTabChange = (_e: React.MouseEvent<HTMLDivElement>, { activeIndex, panes }: TabProps) => {
    // Semantic types `activeIndex` as `number | string | undefined` and `panes` as
    // optional, though it always supplies both from a click. `Number(activeIndex)`
    // is the array subscript the runtime already computed — a numeric string
    // indexes an array identically. The `!`s keep the existing throw for an index
    // Semantic did not supply, rather than silently leaving the tab unchanged.
    /* eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- the receiver is a `fhir-sdoh` export whose module ships no usable types */
    setActiveTab(panes![Number(activeIndex)]!.menuItem.name as string);
  };

  const _toggleSidebar = () => {
    setSidebarVisible(!sidebarVisible);
  };

  const [QRID, setQRID] = useState<{ id: string; isNewResponse: boolean }>();
  const updateQRID = (newID: string, isNewResponse: boolean) => {
    setQRID({ id: newID, isNewResponse });
  };

  const options = { shouldRetryOnError: false, revalidateOnFocus: false };

  const [surveySidebar, setSurveySidebar] = React.useState(false);
  const [questionnaireId, setQuestionnaireId] = React.useState<string>();
  // This state holds `/Practitioner` bundle entries; every consumer reads
  // `.resource.id` off them.
  const [newPractitionerData, setNewPractitionerData] = useState<PractitionerEntry[]>([]);
  // Set from the care-coordinator `/Practitioner` search's `onSuccess` with
  // `data.entry`, which is `BundleEntry[] | undefined` — `Bundle.entry` is `0..*`.
  const [careCoordinatorData, setCareCoordinatorData] = useState<BundleEntry[] | undefined>([]);

  const toggleSurveySidebar = (questionnaire_Id: string) => {
    if (questionnaire_Id) {
      if (questionnaire_Id !== '') {
        setQuestionnaireId(questionnaire_Id);
      }
    }
    setSurveySidebar(!surveySidebar);
  };

  const {
    data: careTeamData,
    mutate: careTeamMutate,
    isValidating: isCareTeamLoading,
  } = useSWR<Fetched<fhir4.Bundle>, Error>([`/CareTeam/?subject=${fhirPatient.id}&_sort=-date`, authInfo.token], FHIRFetcher<fhir4.Bundle>, options);

  const practitionerOptions = {
    shouldRetryOnError: false,
    revalidateOnFocus: false,
    revalidateIfStale: false,
    revalidateOnReconnect: false,
  };
  const coordinatorOptions = {
    shouldRetryOnError: false,
    revalidateOnFocus: false,
    revalidateIfStale: false,
    revalidateOnReconnect: false,
    // `FHIRFetcher` resolves `undefined` only for an empty endpoint, and this key is
    // a constant string, so the `!` states an unreachable arm.
    onSuccess: (data: Fetched<Bundle>) => {
      setCareCoordinatorData(data!.entry);
    },
  };

  //careteam
  const { data: practitionerData, isValidating: datasetLoading } = useSWR<Fetched<fhir4.Bundle>, Error>(
    [`/Practitioner?active=true&_count=1000&identifier:not=CC`, authInfo.token],
    FHIRFetcher<fhir4.Bundle>,
    practitionerOptions
  );
  //care coordinators
  const { data: _coordinatorData, isValidating: _datasetLoadingCC } = useSWR<Fetched<fhir4.Bundle>, Error>(
    [`/Practitioner?active=true&_count=1000&identifier=CC&identifier=${flatpatient.org}`, authInfo.token],
    FHIRFetcher<fhir4.Bundle>,
    coordinatorOptions
  );

  const tasksOptions = {
    shouldRetryOnError: false,
    revalidateOnFocus: false,
    onSuccess: (data: Bundle | undefined, _key: string, _config: unknown) => {
      let tempProgramBudgets: ProgramBudget[] | undefined = [];
      tempProgramBudgets =
        data?.entry &&
        asResourceEntries(data?.entry, 'Task')
          ?.filter((task) => {
            return task.resource.code!.coding!.find((code: Coding) => code.code === 'enrollment-action');
          })
          .map((item) => {
            const flatResource = FHIRUtils.parseFHIRTask(item.resource);
            return {
              /*
               * SUSPECTED DEFECT (a non-numeric budget extension is used verbatim) —
               * REPORTED, NOT FIXED. `FlatTask.programFoodBudget` is `unknown`: the
               * producer takes `[0]` of a fhirpath result without inspecting it. This
               * selection keeps whatever it finds whenever it is truthy and compares
               * greater than zero, so a `program-food-budget` extension carrying the
               * STRING `'50'` is stored as the string `'50'` and threaded into the
               * budget counter and the order-cost arithmetic, where `'50' - cost`
               * coerces but `'50' + cost` concatenates.
               *
               * `Number(...)` here names the coercion the `>` operator already
               * performs — relational comparison applies ToNumber to both operands, so
               * the branch taken and the value selected are unchanged for every input
               * (verified against numbers, numeric strings, non-numeric strings, 0,
               * '', null, undefined, booleans, objects and arrays). It is written this
               * way so the comparison type-checks WITHOUT narrowing the stored value,
               * which would change which budget a member is given.
               */
              programFoodBudget:
                flatResource.programFoodBudget && Number(flatResource.programFoodBudget) > 0
                  ? (flatResource.programFoodBudget as number)
                  : (PROGRAMS_BUDGET_MAP?.[flatpatient.org?.toUpperCase()]?.BUDGET ?? 0),
              referralId: flatResource.referralId,
              id: flatResource.id,
              lastUpdated: flatResource.lastUpdated,
              programOrderCost: 0, // Initialize to 0, will be updated later
            };
          });

      setProgramBudget(tempProgramBudgets || []);
    },
  };

  const {
    data: tasksData,
    error: tasksError,
    mutate: tasksMutate,
  } = useSWR<Fetched<fhir4.Bundle>, Error>([`/Task/?patient=${fhirPatient.id}&_count=1000`, authInfo.token], FHIRFetcher<fhir4.Bundle>, tasksOptions);

  // The active enrollment-action task is the single source of truth for program-week dates.
  // When there is no active task (e.g. the member disenrolled and the task was cancelled),
  // dates are cleared so the counter renders "NA" instead of a stale program week.
  useEffect(() => {
    // `resolveEnrollmentProgramDates` wants `{ resource: Task }[]`, which is
    // what narrowing this single-type `/Task/?patient=...` bundle yields.
    //
    // The narrowing is a TYPE-LEVEL cast, not `bundleEntriesOf`. Substituting `[]` for an
    // absent `entry` would itself be inert (the callee's own `!tasks?.length` guard returns
    // `null` for both), but `bundleEntriesOf` ALSO filters on `resource?.resourceType`,
    // which drops entries whose `resource` is absent — permitted by FHIR R4, where
    // `Bundle.entry.resource` is `0..1`. The callee then does
    // `.map((entry) => entry.resource).filter((task) => isEnrollmentActionTask(task) ...)`,
    // and `isEnrollmentActionTask(undefined)` throws. That throw is upstream's behaviour.
    // Filtering instead yields a launch date computed from the SURVIVING tasks, so a
    // bundle carrying one good Task plus one resource-less entry silently produces a
    // plausible-but-unvalidated program week instead of failing.
    //
    // `tasksData?.entry` is passed through exactly as upstream wrote it — the callee's
    // parameter is `TaskBundleEntry[] | undefined` and its `!tasks?.length` guard is what
    // handles the absent case, so no `?? []` is inserted here either.
    const enrollmentDates = resolveEnrollmentProgramDates(asResourceEntries(tasksData?.entry, 'Task'), referralId);
    if (enrollmentDates) {
      setProvenanceCreateDate(enrollmentDates.programStartDate);
      setLastInterventionLaunchDate(enrollmentDates.launchDate);
    } else {
      setProvenanceCreateDate('');
      setLastInterventionLaunchDate('');
    }
  }, [tasksData, referralId]);

  const refreshTasks = () => {
    // Revalidate the patient-level task bundle used by the program week counter.
    void tasksMutate();
    // `refreshTasks` asserted rather than optional-called: an absent handle throws
    // here today, and a `?.()` would silently skip the refresh.
    (tasksRef?.current as { refreshTasks: () => void } | undefined)?.refreshTasks();
  };

  // Filter all practitioners that have only 32 digits guid as ID (not email id as GUID)
  //
  // SUSPECTED DEFECT — the guard is `if (practitionerData)`, but `Bundle.entry`
  // is `0..*` and is genuinely absent when a `/Practitioner` search matches
  // nothing. `practitionerData.entry.filter(...)` then throws
  // "Cannot read properties of undefined (reading 'filter')" inside an effect,
  // which React surfaces as a render failure for the whole patient view rather
  // than an empty practitioner list. Reported, not fixed: substituting `?? []`
  // would render the assign-task and care-team pickers silently empty, and
  // whether a practitioner-less deployment should degrade or fail loudly is a
  // clinical call. `entry` is asserted here so the existing throw is preserved
  // exactly.
  //
  // `Practitioner.id` is likewise `0..1` in FHIR and asserted for the same
  // reason: `.length` on an absent id throws today, and defaulting it would
  // silently reclassify an id-less practitioner as "not a GUID" and drop them.
  //
  // The resource-type narrowing is applied with `asResourceEntries` (a type-level
  // cast) rather than `.filter(isResourceEntry(...))` for the same
  // preserve-the-throw reason: the runtime predicate also drops entries whose
  // `resource` is absent, which upstream would have thrown on.
  useEffect(() => {
    if (practitionerData) {
      const activePractitioners = asResourceEntries(practitionerData.entry!, 'Practitioner').filter(
        (practitioner) => practitioner.resource.id!.length > 30
      );
      setNewPractitionerData(activePractitioners);
    }
  }, [practitionerData]);

  const profileOptions = {
    shouldRetryOnError: false,
    revalidateOnFocus: false,
    onSuccess: (data: Bundle | undefined, _key: string, _config: unknown) => {
      setProfileData(data);
      /*
       * The three projections below all walk the SAME `/Observation` bundle, and each
       * was annotated `.filter((observation: ResourceEntry<'Observation'>) => …)` then
       * `.map((item: ResourceEntry<'Task'>) => …)`. Neither annotation narrowed
       * anything — `data.entry` is `BundleEntry[]`, so the parameter annotation was a
       * claim, not a check — and the `'Task'` half of it was simply the wrong resource:
       * these entries are Observations, which is why `item.resource.valueString` (an
       * Observation field absent from Task) type-checked only by virtue of the lie.
       *
       * The narrowing is applied with `asResourceEntries`, which is a TYPE-LEVEL cast and
       * emits nothing. A runtime `.filter(isResourceEntry('Observation'))` was tried here
       * and is NOT behaviour-preserving: its predicate is
       * `entry.resource?.resourceType === 'Observation'`, so it also drops entries whose
       * `resource` is ABSENT — which FHIR R4 permits (`Bundle.entry.resource` is `0..1`),
       * as it permits an `OperationOutcome` entry with `search.mode = "outcome"`. On both
       * of those inputs the unguarded `observation.resource.code` read below throws, and
       * that throw is upstream's behaviour; the filtered form instead yields `[]` and
       * reports "no referrals" for a member who may have them.
       */
      setActiveReferralData(
        data?.entry &&
          asResourceEntries(data?.entry, 'Observation')
            ?.filter((observation) => {
              return (
                observation.resource.code.coding!.find((code: Coding) => code.code === 'REFERRAL-TYPE') &&
                ['NEW', 'OUTREACH', 'IN-PROGRESS', 'PENDING'].includes(
                  FHIRUtils.parseFHIRPreference(observation.resource).referralStatus
                )
              );
            })
            .map((item) => {
              return {
                type: item.resource.valueString,
                status: FHIRUtils.parseFHIRPreference(item.resource).referralStatus,
              };
            })
      );
      setActiveAndCompletedReferralData(
        data?.entry &&
          asResourceEntries(data?.entry, 'Observation')
            ?.filter((observation) => {
              return (
                observation.resource.code.coding!.find((code: Coding) => code.code === 'REFERRAL-TYPE') &&
                ['NEW', 'OUTREACH', 'IN-PROGRESS', 'PENDING', 'COMPLETED'].includes(
                  FHIRUtils.parseFHIRPreference(observation.resource).referralStatus
                )
              );
            })
            .map((item) => {
              return {
                type: item.resource.valueString,
                status: FHIRUtils.parseFHIRPreference(item.resource).referralStatus,
              };
            })
      );
      let tempReferralData: ReferralSummary[] | undefined = [];
      tempReferralData =
        data?.entry &&
        asResourceEntries(data?.entry, 'Observation')
          ?.filter((observation) => {
            return observation.resource.code.coding!.find((code: Coding) => code.code === 'REFERRAL-TYPE');
          })
          .map((item) => {
            const flatResource = FHIRUtils.parseFHIRPreference(item.resource);
            return {
              type: item.resource.valueString,
              status: flatResource.referralStatus,
              referralId: flatResource.referralId,
              id: item.resource.id,
              referralDate: flatResource.lastUpdated,
              orgId: flatResource.orgId,
            };
          });

      setAllReferralData(tempReferralData);

      /*
       * SUSPECTED DEFECT — REPORTED, NOT FIXED. Third of FOUR copies of the same
       * predicate: `food/orders/index.tsx` and `food/orders/add.tsx` carry two more
       * inline, and `isInProgress` in `food/catalog/item/foodBundleItems.tsx` is the
       * fourth, extracted rather than inline with its parameter deliberately left
       * unannotated so the same crash survives there.
       * `ReferralSummary.status` is optional, so a referral row without one throws
       * inside the `filter`. All four must change together or not at all — guarding
       * one converts a loud crash into a silently empty referral list at that site
       * alone, which bills the order to no referral.
       */
      const referralData = tempReferralData?.filter((item) => item.status!.toUpperCase() === 'IN-PROGRESS');
      /*
       * SUSPECTED DEFECT — REPORTED, NOT FIXED. `ReferralSummary.referralId` is
       * optional, so a matched in-progress referral without one writes `undefined`
       * into a state declared `string | false`. The banner forwards it to
       * `ProfileCounter.referralId`, whose budget lookup then silently takes its
       * fallback arm rather than the referral's budget. The cast preserves that; a
       * `?? ''` would emit a real coalesce.
       */
      setReferralId(referralData && referralData.length > 0 ? (referralData[0]?.referralId as string) : '');
    },
  };

  //This query narrows things down to profile observations. Labs have different formats when fetching, and need two queries that are then combined
  const { mutate: profileMutate, isValidating: isProfileLoading } = useSWR<Fetched<fhir4.Bundle>, Error>(
    [
      `/Observation/` +
        `?patient=${fhirPatient.id}` +
        `&_sort=-date` +
        `&status:not=entered-in-error` +
        `&identifier:not=https://projectwell.io/fhir/identifiers/referral-status|ENTERED-IN-ERROR`,
      // `&code=${
      //   profileCodes.map((p) => p.code).join(',') +
      //   ',' +
      //   labCodes.map((l) => l.code).join(',') +
      //   ',' +
      //   vitalCodes.map((v) => v.code).join(',') +
      //   ',' +
      //   maternityHealthCodes.map((m) => m.code).join(',')
      // }`,
      // }&category:not=laboratory`,
      authInfo.token,
    ],
    FHIRFetcher<fhir4.Bundle>,
    profileOptions
  );

  const { data: consentData, mutate: consentDataMutate } = useSWR<Fetched<fhir4.Bundle>, Error>(
    [`/Observation/?patient=${fhirPatient.id}&_sort=-date&code=CONSENT&status:not=entered-in-error`, authInfo.token],
    FHIRFetcher<fhir4.Bundle>,
    options
  );

  //fetch medications
  const {
    data: medicationsData,
    error: _medicationsError,
    mutate: medicationsMutate,
  } = useSWR<Fetched<fhir4.Bundle>, Error>([`/MedicationRequest/?patient=${fhirPatient.id}`, authInfo.token], FHIRFetcher<fhir4.Bundle>, options);

  const surveyDataOptions = {
    shouldRetryOnError: false,
    revalidateOnFocus: false,
    onSuccess: (dataset: Bundle | undefined, _key: string, _config: unknown) => {
      if (dataset && dataset.entry && !surveyDataError) {
        setMobileUser(dataset.entry.length ? true : false);
      } else if (dataset && !dataset.entry && !surveyDataError) {
        setMobileUser(false);
      } else if (surveyDataError) {
        setMobileUser(false);
      }
    },
  };

  const { data: _surveyData, error: surveyDataError } = useSWR<Fetched<fhir4.Bundle>, Error>(
    ['/Consent/?patient=' + fhirPatient.id + '&identifier=Member App&_count=1', authInfo.token],
    FHIRFetcher<fhir4.Bundle>,
    surveyDataOptions
  );

  //fetch careplan
  const {
    data: carePlanData,
    error: _carePlanError,
    mutate: carePlanMutate,
  } = useSWR<Fetched<fhir4.Bundle>, Error>([`/CarePlan/?patient=${fhirPatient.id}`, authInfo.token], FHIRFetcher<fhir4.Bundle>, options);

  //fetch questionnaires
  const { data: questionnaireData } = useSWR<Fetched<fhir4.Bundle>, Error>(
    [`/Questionnaire/?context=${flatpatient.org}&context:not=FOOD_RATING&_sort=title`, authInfo.token],
    FHIRFetcher<fhir4.Bundle>,
    options
  );

  const { data: foodSurveyData } = useSWR<Fetched<fhir4.Bundle>, Error>(
    [`/Questionnaire/?identifier=${fhirPatient.id}`, authInfo.token],
    FHIRFetcher<fhir4.Bundle>,
    options
  );

  //CB Emails
  /*
   * The `entry` arm admits `null` because the `onSuccess` below writes one:
   * `dataset.entry = dataset.entry?.length > 0 ? dataset.entry : null`. FHIR's
   * `Bundle` does not allow that, so the state is `Bundle` with `entry`
   * replaced rather than `Bundle` itself — the value handed to `PatientBanner`
   * really can carry a null `entry`, and the banner's two render guards
   * (`props.communicationsData?.entry &&`) are what stand between that null and
   * a `.map` on it.
   */
  const [communicationsData, setCommunicationsData] =
    useState<CommunicationsBundle | undefined>();
  const communicationsDataOptions = {
    shouldRetryOnError: false,
    revalidateOnFocus: false,
    /*
     * Two pre-existing gaps `Bundle` cannot express, each stated by a cast:
     *
     *  - the filter predicate declares `ResourceEntry<'Communication'>` while nothing
     *    checks `resourceType`, so a bundle carrying another resource has
     *    `.resource.medium` read off it;
     *  - `entry` is then set to `null` — the child's "loaded, nothing to show" state,
     *    distinct from both `[]` and an absent key, and the reason
     *    `CommunicationsBundle` exists.
     */
    onSuccess: (dataset: Fetched<Bundle>, _key: string, _config: unknown) => {
      if (dataset && dataset.entry && !communicationsError) {
        // filter out zoom
        // The rule reads the RECEIVER (`entry`, which accepts `BundleEntry[]`); the
        // cast is for the CALLBACK's declared parameter, and removing it fails tsc.
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
        dataset.entry = (dataset.entry as ResourceEntry<'Communication'>[])?.filter(
          (communication: ResourceEntry<'Communication'>) =>
            !communication.resource.medium?.find((m: CodeableConcept) => m.text === 'Zoom') &&
            !communication.resource.medium?.find((m: CodeableConcept) => m.text === 'Zendesk')
        ) as BundleEntry[];
        dataset.entry = (dataset.entry?.length > 0 ? dataset.entry : null) as BundleEntry[];
        setCommunicationsData(dataset as CommunicationsBundle);
      }
    },
  };
  const {
    //data: communicationsData,
    error: communicationsError,
    mutate: communicationsMutate,
    isValidating: isCommunicationsLoading,
  } = useSWR<Fetched<fhir4.Bundle>, Error>(
    ['/Communication/?patient=' + fhirPatient.id + '&medium:text=email', authInfo.token],
    FHIRFetcher<fhir4.Bundle>,
    communicationsDataOptions
  );
  let urlConnect: string;
  /* eslint-disable-next-line @typescript-eslint/no-unused-expressions -- short-circuit used as a statement; the rule's only fix is an `if`, which rewrites the expression rather than its type */
  fhirPatient
    ? (urlConnect =
        '/Communication/?_sort=-sent&patient=' + fhirPatient.id + '&status=completed&_content=Amazon Connect')
    : (urlConnect = '');
  const {
    data: connectData,
    error: _connectError,
    mutate: connectMutate,
  } = useSWR<Fetched<fhir4.Bundle>, Error>([urlConnect, authInfo.token], FHIRFetcher<fhir4.Bundle>, options);

  /* useEffect(() => {
    profileMutate();
  }, [props]); */

  const tabPanes = [
    {
      menuItem: {
        key: 'careplan',
        color: 'blue',
        content: 'CARE PLAN',
        name: 'CARE_PLAN',
      },
      pane: {
        key: 'careplans',
        content: (
          <CarePlanComp
            fhirPatient={fhirPatient}
            patientId={fhirPatient.id}
            patientName={FHIRUtils.parseFHIRPatient(fhirPatient).firstName}
            patientEmail={FHIRUtils.parseFHIRPatient(fhirPatient).email}
            patientPhone={
              // The `?.` is upstream's and guards the ELEMENT, not the array — an
              // empty `mobilePhone` takes the `homePhone` arm rather than throwing.
              /* eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain -- the `?.` and the `!` guard DIFFERENT values here — the chain guards the container, the `!` an inner field FHIR leaves optional. Both are upstream's */
              flatpatient.mobilePhone && (flatpatient.mobilePhone[0] as string | undefined)?.length! > 0
                ? flatpatient.mobilePhone
                : flatpatient.homePhone
            }
            org={flatpatient.org}
            // @ts-expect-error TS2322 — dead prop the child does not declare or read.
            // Left passed: dropping it changes the object React renders with. Same at
            // the `Food` and `Questionnaire` renders below.
            activeTab={props.activeTab}
            toggleSurveySidebar={toggleSurveySidebar}
            practitionerData={newPractitionerData}
            careTeamMutate={careTeamMutate}
            communicationsMutate={communicationsMutate}
            referralData={activeReferralData}
            profileMutate={profileMutate}
            questionnaireData={questionnaireData} //pass questionnaire data for web survey
            tasksData={tasksData}
            tasksError={tasksError}
            tasksMutate={tasksMutate}
            ref={tasksRef}
            careTeamData={careTeamData}
            refreshPatientDetails={props.refreshPatientDetails}
            allReferralData={allReferralData}
            isCommunicationsLoading={isCommunicationsLoading}
            isCareTeamLoading={isCareTeamLoading}
            isRefreshPatientDetailsLoading={props.isRefreshPatientDetailsLoading}
            isProfileLoading={isProfileLoading}
          />
        ),
      },
    },
    // {
    //   menuItem: {
    //     key: 'profile',
    //     color: 'blue',
    //     content: 'PROFILE',
    //     name: 'PROFILE',
    //   },
    //   pane: {
    //     key: 'profiles',
    //     content: (
    //       <Tab.Pane style={{ overflow: 'auto' }}>
    //         <Profile
    //           patientId={fhirPatient.id}
    //           org={flatpatient.org}
    //           profileData={profileData}
    //           setProfileData={setProfileData}
    //           profileMutate={profileMutate}
    //           activeTab={activeTab}
    //           sidebar={false}
    //           setActiveTab={setActiveTab}
    //           updateQRID={updateQRID}
    //           medicationsData={medicationsData}
    //           medicationsMutate={medicationsMutate}
    //           carePlanData={carePlanData}
    //           carePlanMutate={carePlanMutate}
    //           consentData={consentData}
    //           consentDataMutate={consentDataMutate}
    //         />
    //       </Tab.Pane>
    //     ),
    //   },
    // },
    {
      menuItem: {
        key: 'survey',
        color: 'blue',
        content: 'SURVEYS',
        name: 'SURVEYS',
      },
      pane: {
        key: 'surveys',
        content: (
          // The bundle props are `Bundle` here and structurally narrower in
          // `QuestionnairesProps`. Nothing checks `resourceType` on either side, so the
          // narrowing is the child's claim about what the search returns, not a
          // guarantee this caller can make. Same at the `Questionnaire` render below.
          <Questionnaires
            questionnaireData={questionnaireData as QuestionnairesProps['questionnaireData']}
            foodSurveyData={foodSurveyData as QuestionnairesProps['foodSurveyData']}
            patientId={fhirPatient.id}
            patientEmail={flatpatient.email}
            patientMobilePhone={flatpatient.mobilePhone}
            patientFirstName={flatpatient.firstName}
            profileMutate={profileMutate}
            org={flatpatient.org}
            QRID={QRID}
            consentDataMutate={consentDataMutate}
            tasksMutate={tasksMutate}
            doNotContact={doNotContact}
            careTeamData={careTeamData}
            refreshTasks={refreshTasks}
            fhirPatient={fhirPatient}
            activeTab={activeTab}
            refreshPatientDetails={props.refreshPatientDetails}
            practitionerData={newPractitionerData}
            setQRID={setQRID as QuestionnairesProps['setQRID']}
            allReferralData={allReferralData}
            tasksData={tasksData as QuestionnairesProps['tasksData']}
          />
        ),
      },
    },
    {
      menuItem: {
        key: 'notes',
        color: 'blue',
        content: 'NOTES',
        name: 'NOTES',
      },
      pane: {
        key: 'notes',
        content: (
          <Notes
            type={activeTab}
            patientId={fhirPatient.id}
            org={flatpatient.org}
            activeTab={activeTab}
            practitionerData={newPractitionerData}
            tasksData={tasksData}
            tasksMutate={tasksMutate}
            allReferralData={allReferralData}
            connectData={connectData}
            connectMutate={connectMutate}
          />
        ),
      },
    },
    {
      menuItem: {
        key: 'communication',
        color: 'blue',
        content: 'COMMUNICATION',
        name: 'COMMUNICATION',
      },
      pane: {
        key: 'communications',
        content: (
          <Communications
            activeTab={props.activeTab}
            patient={flatpatient}
            carePlanData={carePlanData}
            connectData={connectData}
          />
        ),
      },
    },
    {
      menuItem: {
        key: 'orders',
        color: 'blue',
        content: 'FOOD ORDERING',
        name: 'ORDERS',
      },
      pane: {
        key: 'orderss',
        content: (
          <Food
            patientId={fhirPatient.id}
            patient={flatpatient}
            profileData={profileData}
            org={flatpatient.org}
            // @ts-expect-error TS2322 — dead prop, see the note at the CarePlan render.
            activeTab={props.activeTab}
            allReferralData={allReferralData}
            programBudget={programBudget}
            setProgramBudget={setProgramBudget}
          />
        ),
      },
    },
    {
      menuItem: {
        key: 'foodcard',
        color: 'blue',
        content: 'FOOD CARD',
        name: 'FOOD_CARD',
      },
      pane: {
        key: 'foodcard',
        content: (
          <FoodCard
            // `FoodCard` declares `string | null`; `Patient.id` is `0..1`, so the
            // producer sends `undefined`. The two spellings of absence disagree —
            // `null` here would be a different value, not a narrowing.
            patientId={fhirPatient.id as string | null}
            patient={flatpatient}
            profileData={profileData}
            org={flatpatient.org}
            allReferralData={allReferralData}
            programBudget={programBudget}
            setProgramBudget={setProgramBudget}
            fetchOrders={activeTab === 'FOOD_CARD'}
          />
        ),
      },
    },
    {
      menuItem: {
        key: 'activitylog',
        color: 'blue',
        content: 'ACTIVITY LOG',
        name: 'ACTIVITY_LOG',
      },
      pane: {
        key: 'activitylogs',
        content: <ActivityLog patientId={fhirPatient.id} activeTab={activeTab} />,
      },
    },
    {
      menuItem: {
        key: 'referrals',
        color: 'blue',
        content: 'REFERRALS',
        name: 'REFERRALS',
      },
      pane: {
        key: 'referrals',
        content: (
          <Referrals
            patientId={fhirPatient.id}
            org={flatpatient.org}
            activeTab={activeTab}
            profileMutate={profileMutate}
          />
        ),
      },
    },
  ];

  return (
    <>
      <Modal
        basic
        onClose={() => setRedFlagPopUpPage(false)}
        onOpen={() => setRedFlagPopUpPage(true)}
        open={redFlagPopUpPage}
        size="small"
      >
        <Header icon>
          <Icon className="flag icon" color="red" />
          Red flags!!
        </Header>
        <Modal.Content>
          <p>This patient has red flags!! Please double check them before you proceed.</p>
        </Modal.Content>
        <Modal.Actions>
          <Button color="green" inverted onClick={() => setRedFlagPopUpPage(false)}>
            <Icon name="checkmark" /> OK
          </Button>
        </Modal.Actions>
      </Modal>
      <Grid cols={2} padded={'horizontally'} stretched>
        <Grid.Row divided>
          <Grid.Column width="4">
            {/* <Grid.Column width={4} style={{ maxHeight: '100vh', minHeight: '90vh', overflow: 'auto' }}> */}
            <Segment>
              <PatientBanner
                careTeamData={careTeamData}
                careTeamMutate={careTeamMutate}
                patient={fhirPatient}
                refreshPatientDetails={props.refreshPatientDetails}
                setSelectedPatientInfo={setFlatpatient}
                profileData={profileData}
                profileMutate={profileMutate}
                communicationsData={communicationsData}
                communicationsMutate={communicationsMutate}
                questionnaireData={questionnaireData} //pass questionnaire data for web survey
                referralData={activeAndCompletedReferralData} // pass referral type for web survey
                carePlanData={carePlanData}
                carePlanMutate={carePlanMutate}
                setRedFlagWarning={setRedFlagWarning}
                redFlagWarning={redFlagWarning}
                setFlagOpen={setFlagOpen}
                setDoNotContact={setDoNotContact}
                doNotContact={doNotContact}
                careCoordinatorData={careCoordinatorData}
                provenanceCreateDate={provenanceCreateDate}
                mobileUser={mobileUser}
                lastInterventionLaunchDate={lastInterventionLaunchDate}
                referralId={referralId}
                programBudget={programBudget}
              />
              <br />
              <Segment style={{ maxHeight: '70vh', minHeight: '60vh', overflow: 'auto' }}>
                <b>PROFILE</b>
                <Profile
                  patientId={fhirPatient.id}
                  org={flatpatient.org}
                  profileData={profileData}
                  profileMutate={profileMutate}
                  medicationsData={medicationsData}
                  medicationsMutate={medicationsMutate}
                  consentData={consentData}
                  consentDataMutate={consentDataMutate}
                  setActiveTab={setActiveTab}
                  setProfileData={setProfileData}
                  activeTab={activeTab}
                  setQRID={setQRID}
                  updateQRID={updateQRID}
                  carePlanData={carePlanData}
                  carePlanMutate={carePlanMutate}
                  zipCode={flatpatient.delivery_zip}
                />
                <Zoom
                  patient={flatpatient}
                  patientId={fhirPatient.id}
                  practitionerData={newPractitionerData}
                  loading={datasetLoading}
                  org={flatpatient.org}
                  // `Zoom` restates `profileData.entry` as required and its resources
                  // as `Record<string, unknown>`; the parent holds the SWR `Bundle`,
                  // whose `entry` is `0..*`. Nothing checks either on the way through.
                  profileData={profileData as ProfileBundleForCommPreference}
                />
              </Segment>
            </Segment>
          </Grid.Column>
          <Grid.Column width="12">
            <Segment>
              <Sidebar.Pushable className="cb_sidebar_block">
                <Sidebar.Pusher style={{ marginTop: '-5px', marginLeft: '-0px' }}>
                  {activeTab !== 'SURVEYS' ? (
                    <Tab
                      className="cb_pushable_tabs"
                      defaultActiveIndex={0}
                      menu={{ key: 'pusher', secondary: true, pointing: true }}
                      panes={tabPanes}
                      renderActiveOnly={false}
                      onTabChange={handleTabChange}
                    />
                  ) : (
                    <Tab
                      className="cb_pushable_tabs"
                      defaultActiveIndex={0}
                      menu={{ key: 'pusher', secondary: true, pointing: true }}
                      panes={tabPanes}
                      renderActiveOnly={false}
                      activeIndex={1}
                      onTabChange={handleTabChange}
                    />
                  )}
                </Sidebar.Pusher>
                <Sidebar
                  as={Segment}
                  direction="right"
                  animation="push"
                  icon="labeled"
                  visible={surveySidebar}
                  width="very wide"
                >
                  {questionnaireData && questionnaireId && questionnaireId !== '' && (
                    <Questionnaire
                      // SUSPECTED DEFECT — same shape as the practitioner
                      // effect above: the guard is `questionnaireData &&`, but
                      // `Bundle.entry` is `0..*` and absent when the
                      // `/Questionnaire/?context=...` search matches nothing,
                      // so `.entry.filter(...)` throws during render. `entry`
                      // is asserted to keep that throw rather than substituting
                      // `[]`, which would pass `undefined` to `<Questionnaire>`
                      // and render an empty survey instead of failing.
                      //
                      // The `x[0] && x[0].resource` idiom is retained verbatim
                      // (it yields `undefined` when no questionnaire matches
                      // the id); only the resource-type narrowing is added, so
                      // `.resource` is a `Questionnaire` rather than the whole
                      // `FhirResource` union. That narrowing is a TYPE-LEVEL cast
                      // (`asResourceEntries`), not a runtime
                      // `.filter(isResourceEntry(...))`: the runtime predicate also
                      // drops entries whose `resource` is absent, which would turn
                      // the throw this comment preserves into a silently empty survey.
                      questionnaire={
                        (asResourceEntries(questionnaireData.entry!, 'Questionnaire').filter(
                          (questionnaire) => questionnaire.resource.id === questionnaireId
                        )[0] &&
                          asResourceEntries(questionnaireData.entry!, 'Questionnaire').filter(
                            (questionnaire) => questionnaire.resource.id === questionnaireId
                          )[0]!.resource) as QuestionnaireProps['questionnaire']
                      }

                      patientId={fhirPatient.id!}
                      toggleSurveySidebar={toggleSurveySidebar as (value: unknown) => void}
                      profileMutate={profileMutate}
                      practitionerData={newPractitionerData}
                      org={flatpatient.org}
                      tasksMutate={tasksMutate}
                      // @ts-expect-error TS2322 — dead prop, see the note at the
                      // CarePlan render.
                      doNotContact={doNotContact}
                      fhirPatient={fhirPatient}
                      refreshPatientDetails={props.refreshPatientDetails}
                      refreshTasks={refreshTasks}
                      setQRID={setQRID}
                      tasksData={tasksData as QuestionnairesProps['tasksData']}
                    />
                  )}
                </Sidebar>
              </Sidebar.Pushable>
            </Segment>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </>
  );
});

export default Patient360;
